/**
 * Shared facts about the Easy-Peasy.AI MCP server.
 *
 * Used by the public setup page (`/mcp`) and by the MCP section of
 * Settings → API, so the endpoint and the client snippets can never drift
 * apart. The server itself lives in `src/pages/api/mcp.ts`; the full write-up
 * is in `docs/mcp-server.md`.
 */

import { MUSE_CONNECT_PROMPT } from '@/constants/museConnector';

export const MCP_ENDPOINT = 'https://easy-peasy.ai/mcp';

/** Placeholder shown in snippets before a real key is substituted. */
export const MCP_API_KEY_PLACEHOLDER = 'YOUR_API_KEY';

export type McpClientKey =
  | 'claude'
  | 'chatgpt'
  | 'muse'
  | 'claude-code'
  | 'cursor'
  | 'windsurf';

export interface McpClientSetup {
  key: McpClientKey;
  label: string;
  /** OAuth clients need no key; CLI/editor clients send it as a header. */
  usesApiKey: boolean;
  steps: string[];
  snippetLabel: string;
  buildSnippet: (apiKey: string) => string;
}

export const MCP_CLIENT_SETUPS: McpClientSetup[] = [
  {
    key: 'claude',
    label: 'Claude',
    usesApiKey: false,
    steps: [
      'In claude.ai or Claude Desktop, open Settings → Connectors → Add custom connector.',
      'Paste the connector URL below and click Add.',
      'Click Connect, sign in to Easy-Peasy.AI, and approve — no API key needed.',
    ],
    snippetLabel: 'Connector URL',
    buildSnippet: () => MCP_ENDPOINT,
  },
  {
    key: 'chatgpt',
    label: 'ChatGPT',
    usesApiKey: false,
    steps: [
      'Open the ChatGPT Plugins Directory.',
      'Find Easy-Peasy.AI and select Add.',
      'Sign in to Easy-Peasy.AI and approve access — no API key needed.',
    ],
    snippetLabel: 'Plugin server URL',
    buildSnippet: () => MCP_ENDPOINT,
  },
  {
    key: 'muse',
    label: 'Meta Muse',
    // Muse builds the connector itself from this message and collects the
    // key through its own secure credentials prompt — never embed the key
    // in the message, it would land in the chat transcript.
    usesApiKey: false,
    steps: [
      'Copy the message below and send it to Muse — it builds the connector from our MCP server.',
      'When Muse asks for your API key, paste the key above into its secure credentials prompt (never into the chat).',
      'Muse tests the connection and saves it as a skill.',
    ],
    snippetLabel: 'Message to Muse',
    buildSnippet: () => MUSE_CONNECT_PROMPT,
  },
  {
    key: 'claude-code',
    label: 'Claude Code',
    usesApiKey: true,
    steps: [
      'Run this command in your terminal.',
      'Ask Claude to generate an image, write with a template, or create a video.',
    ],
    snippetLabel: 'Terminal',
    buildSnippet: (apiKey) =>
      `claude mcp add easy-peasy --transport http ${MCP_ENDPOINT} --header "x-api-key: ${apiKey}"`,
  },
  {
    key: 'cursor',
    label: 'Cursor',
    usesApiKey: true,
    steps: [
      'Open Cursor Settings → MCP → Add new global MCP server.',
      'Paste this into your mcp.json.',
      'Ask the agent to use the Easy-Peasy tools.',
    ],
    snippetLabel: 'mcp.json',
    buildSnippet: (apiKey) => `{
  "mcpServers": {
    "easy-peasy": {
      "url": "${MCP_ENDPOINT}",
      "headers": { "x-api-key": "${apiKey}" }
    }
  }
}`,
  },
  {
    key: 'windsurf',
    label: 'Windsurf',
    usesApiKey: true,
    steps: [
      'Open Windsurf Settings → Cascade → MCP servers.',
      'Add this to your mcp_config.json.',
      'Cascade can now generate content on Easy-Peasy.AI.',
    ],
    snippetLabel: 'mcp_config.json',
    buildSnippet: (apiKey) => `{
  "mcpServers": {
    "easy-peasy": {
      "serverUrl": "${MCP_ENDPOINT}",
      "headers": { "x-api-key": "${apiKey}" }
    }
  }
}`,
  },
];

/** The tools an MCP client discovers, mirroring `src/lib/mcpTools.ts`. */
export const MCP_TOOLS: { name: string; description: string }[] = [
  { name: 'get_account', description: 'Check that the connection works.' },
  { name: 'list_templates', description: 'List writing templates.' },
  { name: 'get_template', description: 'Get one template’s input fields.' },
  { name: 'generate_text', description: 'Run a template and get text back.' },
  { name: 'generate_image', description: 'Generate or edit an image.' },
  { name: 'get_image', description: 'Fetch a generated image.' },
  { name: 'generate_video', description: 'Start a video generation.' },
  { name: 'get_video', description: 'Poll a video until it is ready.' },
  { name: 'display_media', description: 'Show saved images or videos inline.' },
  { name: 'list_voices', description: 'List text-to-speech voices.' },
  { name: 'generate_speech', description: 'Turn text into speech.' },
  { name: 'transcribe_audio', description: 'Start an audio transcription.' },
  { name: 'get_transcription', description: 'Poll a transcription.' },
];
